import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import DataGrid, {
  Column,
  ColumnChooser,
  DataGridRef,
  Export,
  FilterBuilderPopup,
  FilterPanel,
  FilterRow,
  Grouping,
  GroupItem,
  GroupPanel,
  HeaderFilter,
  Pager,
  Paging,
  Search,
  SearchPanel,
  Selection,
  SortByGroupSummaryInfo,
  StateStoring,
  Summary,
  Lookup,
  Item,
  ColumnChooserSelection,
  Toolbar,
  MasterDetail,
} from "devextreme-react/data-grid";
import { OnExporting } from "utils/dataGridTools";
import CustomStore from "devextreme/data/custom_store";
import { RowClickEvent } from "devextreme/ui/data_grid";
import {
  GridRowContextMenuTypes,
  TContextMenuAction,
  TDataTableColumn,
  TGridRowContextMenu,
} from "./types";
import { useTranslation } from "react-i18next";
import style from "./tesGrid.module.scss";
import { HorizontalAlignment, ToolbarItemLocation } from "devextreme/common";
import { useClearGridSelectionOnHeaderUncheck } from "components/Grid/hooks/useClearGridSelectionOnHeaderUncheck";
import clsx from "clsx";
import {
  GridTypeNameKeys,
  GridTypes,
} from "components/Grid/grids/constants";
import { toast } from "react-toastify";
import { ConfirmationModal } from "components/ConfirmationModal";

type TProps = {
  className?: string;
  id?: string;
  gridKey: string;
  dataSource: CustomStore | Array<any>;
  onRowClick?: (e: RowClickEvent) => void;
  storageKey?: GridTypes;
  /** Display name used for export file names; falls back to the storageKey grid type name. */
  gridName?: string;
  hasSearchPanel: boolean;
  columns: TDataTableColumn[];
  rowContextMenu?: TGridRowContextMenu;
  hasSelectableRow?: boolean;
  hasGroupingFeatures?: boolean;
  hasFilterBuilder?: boolean;
  hasPagination?: boolean;
  keyExpr?: string;
  clearAllSelectionOnHeaderUncheck?: boolean;
  columnAutoWidth?: boolean;
  height?: string | number;
  disabled?: boolean;
  onSelectionChanged?: (selectedRows: any) => void;
  selectedRowKeys?: any[];
  isLoading?: boolean;
  masterDetailComponent?: any;
  onRowUpdated?: (e: any) => void;
  toolbarOptions?: {
    location?: ToolbarItemLocation;
    icon?: string;
    text?: string;
    hint?: string;
    className?: string;
    onClick?: (e: any) => void;
  }[];
  allowedChangeScrolling?: boolean;
};
export const Grid = forwardRef<DataGridRef, TProps>((props, ref) => {
  const {
    id = "gridContainer",
    className,
    gridKey,
    dataSource,
    onRowClick = (e) => {},
    storageKey,
    gridName,
    hasSearchPanel,
    columns,
    rowContextMenu,
    hasSelectableRow = false,
    hasGroupingFeatures = true,
    hasFilterBuilder = true,
    hasPagination = true,
    keyExpr,
    clearAllSelectionOnHeaderUncheck = false,
    columnAutoWidth = undefined,
    height = "auto",
    selectedRowKeys = undefined,
    disabled = false,
    onSelectionChanged = () => {},
    toolbarOptions,
    allowedChangeScrolling = true,
    isLoading = false,
    masterDetailComponent,
    onRowUpdated,
  } = props;

  const { t } = useTranslation();

  const innerRef = useRef<DataGridRef>(null);
  useImperativeHandle(ref, () => innerRef.current!, []);

  useClearGridSelectionOnHeaderUncheck(id, clearAllSelectionOnHeaderUncheck);

  const initDoneRef = useRef(false); // have we finished initial selection for all keys?

  const [allowedScrolling, setAllowedScrolling] = useState(!!columnAutoWidth);
  const [isResetConfirmOpen, setIsResetConfirmOpen] = useState(false);

  const resetGridSettings = () => {
    if (storageKey) {
      localStorage.removeItem(storageKey.toString());
    }
    innerRef.current?.instance().state(null);
    setIsResetConfirmOpen(false);
    toast.success(t("settingsResetToDefaultSuccessfully"));
  };

  const exportGridName =
    gridName ?? (storageKey ? t(GridTypeNameKeys[storageKey]) : undefined);

  useEffect(() => {
    if (innerRef?.current?.instance()) {
      if (isLoading) {
        innerRef.current.instance().beginCustomLoading("");
      } else {
        innerRef.current.instance().endCustomLoading();
      }
    }
  }, [isLoading]);

  const getDefaultAlignment = (
    col: TDataTableColumn
  ): HorizontalAlignment | undefined => {
    if (col.buttons) {
      return "center";
    }
    if (col.alignment) {
      return col.alignment;
    } else {
      return "right";
    }
  };

  const onContentReady = () => {
    if (!initDoneRef.current && selectedRowKeys && selectedRowKeys.length > 0) {
      initDoneRef.current = true;
      setSelectedRows(selectedRowKeys);
    }
  };

  const setSelectedRows = (rows: string[]) => {
    innerRef?.current?.instance()?.selectRows(rows, true);
  };

  const generateColumns = (columns: TDataTableColumn[]) => {
    if (columns?.length > 0) {
      return columns.map((col, index) => {
        return (
          <Column
            key={`${col.key}_${index}`}
            dataField={col.dataField}
            caption={col.caption && t(col.caption)}
            visible={col.visible}
            dataType={col.dataType === "time" ? "datetime" : col.dataType}
            format={col.format}
            calculateCellValue={col.calculateCellValue}
            calculateDisplayValue={col.calculateDisplayValue}
            sortOrder={col.sortOrder}
            width={col.width}
            alignment={getDefaultAlignment(col)}
            allowGrouping={col.allowGrouping}
            allowSorting={col.allowSorting}
            allowFiltering={col.allowFiltering}
            allowHeaderFiltering={col.allowHeaderFiltering}
            headerFilter={col.headerFilter}
            // lookup={col.lookup}
            cellRender={col.cellRender}
            visibleIndex={col.visibleIndex}
            sortIndex={col.sortIndex}
            minWidth={col.minWidth}
            //button config
            buttons={col.buttons}
            type={col.buttons && "buttons"}
            fixed={col.buttons && true}
            fixedPosition={col.buttons && "left"}
            calculateFilterExpression={
              col.hasFlag
                ? (
                    filterValue: any,
                    selectedFilterOperation: any,
                    target: any
                  ) => {
                    return [col.dataField, "bitand", filterValue];
                  }
                : undefined
            }
          >
            {col?.columns && generateColumns(col.columns)}
            {col?.lookup && (
              <Lookup
                dataSource={col?.lookup?.dataSource}
                valueExpr={col?.lookup?.valueExpr}
                displayExpr={col?.lookup?.displayExpr}
              />
            )}
          </Column>
        );
      });
    }
  };

  return (
    <>
      <DataGrid
        //TODO refine Styling To Prevent using dx-style with # selector
        className={clsx("w-full", className)}
        id={id}
        key={gridKey}
        ref={innerRef}
        keyExpr={keyExpr}
        dataSource={dataSource}
        rowAlternationEnabled={true}
        showBorders={true}
        onRowClick={onRowClick}
        hoverStateEnabled={true}
        remoteOperations={true}
        allowColumnReordering={true}
        allowColumnResizing={true}
        columnAutoWidth={allowedScrolling}
        height={height}
        disabled={disabled}
        onSelectionChanged={onSelectionChanged}
        onContentReady={onContentReady}
        onRowUpdated={onRowUpdated}
        onContextMenuPreparing={(e) => {
          if (e.row && e.row.rowType === "data" && rowContextMenu) {
            e.items = rowContextMenu?.items.map((item) => ({
              ...item,
              onItemClick: () =>
                rowContextMenu?.onItemClick(
                  item.type as GridRowContextMenuTypes,
                  e.row?.data
                ),
            }));
          }
        }}
        onExporting={(e) => OnExporting(e, exportGridName)}
        // style={{ margin: "0 1rem" }}
        onOptionChanged={(e) => {
          if (e.fullName.split(".")[1] === "visible") e.component.refresh();
        }}
      >
        <Export
          enabled={true}
          allowExportSelectedData={!Array.isArray(dataSource)}
        />
        <Grouping contextMenuEnabled={true} autoExpandAll={false} />
        {hasGroupingFeatures && <GroupPanel visible={true} />}

        {hasFilterBuilder && <FilterPanel visible={true} />}
        {hasFilterBuilder && <FilterBuilderPopup position={"top"} />}

        {storageKey && (
          <StateStoring
            enabled={true}
            type="localStorage"
            storageKey={storageKey.toString()}
            savingTimeout={500}
          />
        )}
        <SortByGroupSummaryInfo summaryItem="Total Count" sortOrder="desc" />
        <Summary>
          <GroupItem summaryType="count" alignByColumn name="Total Count" />
        </Summary>
        {hasPagination && <Paging enabled={true} defaultPageSize={100} />}
        {hasPagination && (
          <Pager
            showPageSizeSelector={true}
            allowedPageSizes={[100, 200, 300, 400, 500]}
            showNavigationButtons={true}
            showInfo={true}
            visible={true}
          />
        )}
        {hasSelectableRow && (
          <Selection
            mode="multiple"
            selectAllMode={"allPages"}
            showCheckBoxesMode={"always"}
            deferred={!Array.isArray(dataSource)}
            //@ts-ignore
            maxFilterLengthInRequest={10000}
          />
        )}
        <FilterRow visible={true} applyFilter="auto" />
        <HeaderFilter visible={true} />
        {hasSearchPanel && (
          <SearchPanel
            visible={true}
            width={285}
            placeholder={t("search...")}
          />
        )}
        <ColumnChooser
          width={350}
          height={400}
          enabled={true}
          mode="select"
          sortOrder="asc"
        >
          <Search enabled />
          <ColumnChooserSelection recursive />
        </ColumnChooser>
        {generateColumns(columns)}

        <Toolbar>
          {toolbarOptions?.map((btn, index) => {
            //TODO
            return null;
          })}
          <Item name="groupPanel" />
          <Item name="searchPanel" />
          {storageKey && (
            <Item
              location="after"
              widget="dxButton"
              options={{
                icon: "revert",
                hint: t("resetToDefault"),
                elementAttr: { id: `${id}_resetGridSettings` },
                onClick: () => setIsResetConfirmOpen(true),
              }}
            />
          )}
          {allowedChangeScrolling && (
            <Item
              location="after"
              widget="dxButton"
              options={{
                icon: "fullscreen",
                hint: t("horizontalScrolling"),
                type: allowedScrolling ? "default" : "normal",
                elementAttr: { id: `${id}_toggleHorizontalScrolling` },
                onClick: () => setAllowedScrolling((prev) => !prev),
              }}
            />
          )}
          <Item name="exportButton" />
          <Item name="columnChooserButton" />
        </Toolbar>
        {masterDetailComponent && (
          <MasterDetail enabled={true} component={masterDetailComponent} />
        )}
      </DataGrid>
      {storageKey && (
        <ConfirmationModal
          isOpen={isResetConfirmOpen}
          title={t("resetToDefault")}
          type={"info"}
          description={t("resetWarningMessage")}
          onSubmit={resetGridSettings}
          onClose={() => setIsResetConfirmOpen(false)}
        />
      )}
    </>
  );
});
